/** Pure OAuth 2.0 / PKCE helpers for the Guardius hosted-page sign-in flow.
 *  No React, no storage, no network — all unit-testable. SIWE itself happens
 *  on Guardius's /auth/authorize page; this module only builds the authorize
 *  URL, mints the PKCE verifier/challenge + state, decodes JWT claims for
 *  client-side gating, and matches permission strings.
 *
 *  Server contract verified 2026-06-07 against
 *  ~/projects/experiments/penseur-guardius/server/src/auth/ (param names) and
 *  .../sdk/typescript/src/core/rbac-permissions.ts (permission matching);
 *  re-verified 2026-06-20 for multi-tenant SSO (Guardius >= 2.37.0: tenant_id
 *  optional on /auth/authorize, platform_id required). */

import type { RuntimeConfig } from './auth';

/** The literal OAuth public-client id for this console. */
export const PLATFORM_ID = 'penseur-trading';

/** Client-side shape check for invite codes; mirrors Guardius's format. */
export const INVITE_CODE_PATTERN = /^gi_[0-9abcdefghjkmnpqrstvwxyz]{26}$/;

/** Typed subset of the Guardius access-token claims the console reads.
 *  No signature verification is done client-side — the console API's Guardius
 *  authorizer is the enforcement point; these claims only drive UI gating. */
export interface Claims {
  sub: string;
  wallet_address?: string;
  /** The Guardius tenant UUID this token is scoped to. Drives the console's
   *  owner/write gating: the permission strings deriveState builds are keyed on
   *  THIS value (the resolved org's tenant), never a client-chosen one. */
  tenant_id?: string;
  permissions?: string[];
  permissions_truncated?: boolean;
  permissions_version?: number;
  exp?: number;
}

/** base64url-encode raw bytes (RFC 4648 §5, no padding). */
function base64UrlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** base64url-decode to a UTF-8 string; throws on malformed input. */
function base64UrlDecode(input: string): string {
  const padded = input.replace(/-/g, '+').replace(/_/g, '/');
  // atob tolerates missing padding in most engines, but normalize for safety.
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  const bin = atob(padded + pad);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

/** Generate a PKCE verifier + S256 challenge. The verifier is a 43-char
 *  base64url string (32 random bytes → no padding); the challenge is
 *  base64url(SHA-256(verifier)). Guardius enforces S256 + the 43-char
 *  base64url challenge shape (authorize.ts:151-163). */
export async function generatePkce(): Promise<{ verifier: string; challenge: string }> {
  const verifierBytes = new Uint8Array(32);
  crypto.getRandomValues(verifierBytes);
  const verifier = base64UrlEncode(verifierBytes);
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  const challenge = base64UrlEncode(new Uint8Array(digest));
  return { verifier, challenge };
}

/** Opaque CSRF state: 32 random bytes → 43-char base64url. Guardius's
 *  /auth/authorize rejects a state shorter than 32 chars with WEAK_STATE
 *  (authorize.ts:135-141), so 16 bytes (22 chars) is too short — use 32. */
export function randomState(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** Decode the payload segment of a JWT into typed claims; `null` on anything
 *  malformed. NO signature verification client-side — UI gating only; the
 *  console API's Guardius authorizer is the authoritative enforcement point. */
export function decodeJwtClaims(token: string): Claims | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const json = base64UrlDecode(parts[1]);
    const claims = JSON.parse(json) as unknown;
    if (typeof claims !== 'object' || claims === null) return null;
    const c = claims as Record<string, unknown>;
    if (typeof c.sub !== 'string') return null;
    return c as unknown as Claims;
  } catch {
    return null;
  }
}

/** Segment-wise match over the 6-segment
 *  `region:platform:tenantId:resourceType:resourceId:action` permission format.
 *
 *  RECONCILIATION (verified 2026-06-07): the Guardius server and SDK use
 *  SYMMETRIC wildcard matching — a `*` in EITHER the granted segment OR the
 *  required segment matches that position
 *  (sdk/typescript/src/core/rbac-permissions.ts:8,
 *   server/src/shared/services/rbac-authorization.ts:134). This is NOT the
 *  naive "granted `*` matches anything" rule; the required string also gets
 *  wildcard treatment. It matters here because the console builds a required
 *  string with a `*` region segment (`*:penseur-trading:<tenant>:...`). Both
 *  sides also return false for any string that isn't exactly 6 segments
 *  (never throw). This implementation matches that semantics exactly so
 *  client-side gating agrees with the server's authorizer. */
export function permissionMatches(granted: string[] | undefined, required: string): boolean {
  if (!granted) return false;
  const c = required.split(':');
  if (c.length !== 6) return false;
  return granted.some((grant) => {
    const g = grant.split(':');
    if (g.length !== 6) return false;
    for (let i = 0; i < 6; i++) {
      if (g[i] !== '*' && c[i] !== '*' && g[i] !== c[i]) return false;
    }
    return true;
  });
}

/** Build the Guardius hosted-page authorize URL.
 *
 *  MULTI-TENANT SSO (Guardius >= 2.37.0): the hosted /auth/authorize ceremony is
 *  tenant-aware, so `tenant_id` is OPTIONAL and `platform_id` is the login scope.
 *  The console serves one origin for all orgs and does NOT know the tenant at
 *  login, so signIn() omits `tenant_id`: Guardius authenticates the wallet,
 *  resolves which of this platform's tenants the identity is a member of, shows a
 *  picker (or auto-selects when there is exactly one), and returns a
 *  tenant-scoped code — which the console exchanges and maps tenant -> org via
 *  GET /whoami + the tenant-index, exactly as before.
 *
 *  - `platform_id` is REQUIRED (the literal console client id). Omitting BOTH
 *    `platform_id` and `tenant_id` -> 400 PLATFORM_REQUIRED.
 *  - `tenant_id` is optional and, when supplied, is a HINT ("switch to org X")
 *    that is still membership-checked. Normal login omits it; pass it only for an
 *    explicit org switch.
 *
 *  Contract: penseur-guardius/docs/integration/multi-tenant-sso-migration.md
 *  (design seed: docs/superpowers/specs/2026-06-18-multi-tenant-sso-login-design.md). */
export function buildAuthorizeUrl(
  config: Pick<RuntimeConfig, 'guardiusUrl'>,
  args: {
    challenge: string;
    state: string;
    redirectUri: string;
    /** Override the login scope. Defaults to this console's own
     *  `penseur-trading` client id; the Watchtower connect ceremony
     *  (lib/watchtower.ts) passes `penseur-watchtower` so the minted code — and
     *  the refresh chain the backend derives from it — belongs to the Watchtower
     *  platform, which is the only platform whose tokens Watchtower accepts. */
    platformId?: string;
    tenantId?: string;
    invite?: string;
    intent?: string;
  },
): string {
  const url = new URL('/auth/authorize', config.guardiusUrl);
  const params = new URLSearchParams({
    response_type: 'code',
    redirect_uri: args.redirectUri,
    code_challenge: args.challenge,
    code_challenge_method: 'S256',
    state: args.state,
    platform_id: args.platformId ?? PLATFORM_ID,
  });
  // Optional tenant HINT for an explicit org switch; normal login omits it and
  // lets the hosted ceremony resolve membership + pick the tenant (>= 2.37.0).
  if (args.tenantId) params.set('tenant_id', args.tenantId);
  // Optional invite code. Guardius (v2.23+) validates it fail-fast pre-auth on
  // /auth/authorize and redeems it after sign-in but before minting the code, so
  // the dashboard only needs to thread the param through (URLSearchParams handles
  // URL-encoding). Absent when the user reaches sign-in without an invite link.
  if (args.invite) params.set('invite', args.invite);
  // Optional sign-in intent. The onboarding wizard passes `intent=onboarding` so
  // Guardius (>= 2.43.0) runs the onboarding-lobby ceremony for a 0-membership
  // wallet — minting a short-lived, refresh-less access token scoped to the
  // platform's system "lobby" tenant (empty permissions[]) instead of
  // dead-ending at /auth/no-tenant. Authorization to create an org remains OUR
  // `oci_` invite, consumed server-side by the intake route; the lobby token is
  // authenticated-but-unauthorized. Normal (existing-org) sign-in omits this.
  // Guide: penseur-guardius/docs/integration/onboarding-lobby.md.
  if (args.intent) params.set('intent', args.intent);
  url.search = params.toString();
  return url.toString();
}
